
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Asset} from '../core/Asset';
import {FieldModelAsset} from '../core/Asset';

/**
 * AssetsGetAllResult struct
 */
class AssetsGetAllResult {
  /**
   * Initialize struct
   * @param {!Array=} assets
   * @constructor
   */
  constructor (assets = []) {
    this.Assets = assets
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AssetsGetAllResult}
   * @param {!AssetsGetAllResult} other Other struct
   * @returns {!AssetsGetAllResult} This struct
   */
  copy (other) {
    if (other.Assets != null) {
      this.Assets = []
      for (let item of other.Assets) {
        if (item != null) {
          let tempItem
          tempItem = Asset.fromObject(item)
          this.Assets.push(tempItem)
        } else {
          this.Assets.push(null)
        }
      }
    } else {
      this.Assets = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AssetsGetAllResult}
   * @returns {!AssetsGetAllResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AssetsGetAllResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AssetsGetAllResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AssetsGetAllResult}
   * @param {!AssetsGetAllResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AssetsGetAllResult)) {
      throw new TypeError('Instance of AssetsGetAllResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AssetsGetAllResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Assets: ((this.Assets != null) ? Array.from(this.Assets, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AssetsGetAllResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AssetsGetAllResult} other Object value
   * @returns {!AssetsGetAllResult} Created struct
   */
  static fromObject (other) {
    return new AssetsGetAllResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AssetsGetAllResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AssetsGetAllResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AssetsGetAllResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10042
  }
}

export { AssetsGetAllResult };

/**
 * Fast Binary Encoding AssetsGetAllResult field model
 */
class FieldModelAssetsGetAllResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Assets = new fbe.FieldModelVector(new FieldModelAsset(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Assets.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Assets.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAssetsGetAllResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10042
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Assets.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Assets.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Assets.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!AssetsGetAllResult} fbeValue Default value, defaults is new AssetsGetAllResult()
   * @returns {!AssetsGetAllResult} AssetsGetAllResult value
   */
  get (fbeValue = new AssetsGetAllResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!AssetsGetAllResult} fbeValue AssetsGetAllResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Assets.fbeSize) <= fbeStructSize) {
      this.Assets.get(fbeValue.Assets)
    } else {
      fbeValue.Assets.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Assets.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAssetsGetAllResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAssetsGetAllResult}
   * @param {!AssetsGetAllResult} fbeValue AssetsGetAllResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAssetsGetAllResult}
   * @param {AssetsGetAllResult} fbeValue AssetsGetAllResult value
   */
  setFields (fbeValue) {
    this.Assets.set(fbeValue.Assets)
  }
}

export { FieldModelAssetsGetAllResult };

/**
 * Fast Binary Encoding AssetsGetAllResult model
 */
class AssetsGetAllResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAssetsGetAllResult(this.buffer, 4)
  }

  /**
   * Get the AssetsGetAllResult model
   * @this {!AssetsGetAllResultModel}
   * @returns {!FieldModelAssetsGetAllResult} model AssetsGetAllResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AssetsGetAllResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AssetsGetAllResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AssetsGetAllResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AssetsGetAllResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAssetsGetAllResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AssetsGetAllResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AssetsGetAllResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AssetsGetAllResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AssetsGetAllResultModel}
   * @param {!AssetsGetAllResult} value AssetsGetAllResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AssetsGetAllResultModel}
   * @param {!AssetsGetAllResult} value AssetsGetAllResult value, defaults is new AssetsGetAllResult()
   * @return {!object} Deserialized AssetsGetAllResult value and its size
   */
  deserialize (value = new AssetsGetAllResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AssetsGetAllResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AssetsGetAllResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AssetsGetAllResultModel}
   * @param {!number} prev Previous AssetsGetAllResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AssetsGetAllResultModel };
